/**
 * clearCoachingViewParam (#2489) — strip the `?view=coaching` deep-link
 * marker (Issue #737/#688) from the CURRENT browser history entry.
 *
 * WHY THIS EXISTS
 * `?view=coaching` is a durable flag ChatPage stamps onto its own URL via
 * `history.replaceState` whenever the four-act/legacy debrief is showing, so
 * a page RELOAD (F5) or an emailed "Review session" link restores the
 * coaching view (#688, #737). Because it is a durable flag rather than a
 * one-shot trigger, it must be actively cleared whenever the learner leaves
 * the debrief — simply navigating away does not clear it, because
 * `pushState`/`history`-based navigation never edits a PAST entry. Left in
 * place, a later browser-BACK to that exact URL sees the stale marker and
 * silently reopens the debrief — always on Act 1 (`DebriefActs` has no
 * act-position memory, see `components/debrief/debriefActsState.js`),
 * regardless of which act the learner had actually reached.
 *
 * Observed by Gilbert on staging, 2026-08-26, run 3574 (#2489): "I think I
 * then pressed back on the browser and landed in an older version of the
 * feedback." Confirmed mechanism: he left via an exit path that does not
 * clear this marker (the globally-mounted Home FAB is the demonstrated one),
 * so the run's `/chat/:runId?view=coaching` history entry survived; a later
 * browser-back to it reopened the debrief on Act 1 rather than wherever he
 * had reached.
 *
 * ChatPage's own dedicated "Exit" control (`handleExitToNextFlow`) and its
 * "Continue this conversation" handler already strip this inline (the latter
 * added for the identical failure mode under #872 — a stale 'completed' read
 * looping the learner back into the eval view). This helper exists because
 * at least two OTHER exit paths reach the same failure independently: the
 * globally-mounted `<HomeButton/>` FAB (#1555 — intentionally decoupled from
 * ChatPage, so it cannot reuse ChatPage's local handler) and several
 * in-ChatPage `navigate()` calls that are not routed through
 * `handleExitToNextFlow` (the four-act "Pathway complete" CTA, the
 * scenario-generated redirect fired from the coach chat). Enumerating and
 * individually patching every such callsite is the "checks at multiple
 * callsites" smell CLAUDE.md's Debugging Discipline names as a sign the root
 * is elsewhere; this single shared primitive is what both fix points
 * (ChatPage's own wrapped `navigate`, and `HomeButton.goHome`) call, instead
 * of re-deriving the same URL-manipulation snippet a third and fourth time.
 *
 * Safe to call unconditionally — a no-op when the param is already absent —
 * and safe outside a browser context (best-effort; swallows URL/History API
 * failures rather than blocking navigation on them).
 */
export function clearCoachingViewParam() {
  try {
    if (typeof window === 'undefined' || !window.location || !window.history?.replaceState) return;
    const url = new URL(window.location.href);
    if (!url.searchParams.has('view')) return;
    url.searchParams.delete('view');
    // #2489/#2564 review-fix: preserve react-router's history state
    // ({usr, key, idx}) instead of replacing it with {}. @remix-run/router
    // reads `globalHistory.state.key || "default"` (router.cjs.js:229) on
    // every popstate, so clobbering it here permanently downgraded this
    // entry's location.key to 'default' — the sentinel the Back-restore gate
    // in ChatPage uses to exclude the ambiguous first-SPA-boot POP. Stripping
    // the query param is the entire job; the state is not ours to discard.
    window.history.replaceState(window.history.state, '', url);
  } catch (_e) {
    // best-effort URL cleanup — never block navigation on it
  }
}
